import { Injectable } from '@angular/core';
import * as _ from 'lodash-es';

/**
 * HTML-escape a value that is about to be concatenated into markup.
 *
 * Escapes both quote characters alongside `&`, `<` and `>`, so a single
 * function serves text content and quoted attribute values alike: an entity
 * renders as the character it stands for in either position, whereas keeping a
 * text variant and an attribute variant apart only creates a chance to reach
 * for the weaker one.
 *
 * Needed wherever a fragment of markup is built by hand -- a growl message, a
 * wiki-link anchor, an expanded tracker macro, a video embed. Content the
 * backend escaped at write time is already safe, but Markdown is stored raw
 * and only sanitised at render time by `gf-markdown-viewer`, so a hand-built
 * fragment has to neutralise its own inputs.
 *
 * A plain exported function rather than a `UtilsService` method, so the
 * DI-free function modules (chat bodies, tracker macros) can share it too.
 */
export function escapeHtml(value: string): string {
    if (!value) {
        return '';
    }
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

@Injectable()
export class UtilsService {
    constructor(private window: Window) { }

    /*
     * Deep compares two objects
     * @param object obj1
     * @param object obj2
     * @returns boolean true if the objects are equal, false otherwise
     */
    equals(obj1, obj2) {
        if (obj1 == null || obj2 == null) {
            if (!(obj1 == null && obj2 == null)) {
                return false;
            }
        }

        var total = _.sum(
            _.map(
                this.getObjectDiff(obj1, obj2),
                _.size
            )
        );
        return (total === 0);
    }

    getUpdate(original: any, updated: any) {
        var diff = this.getObjectDiff(original, updated);
        return _.reduce(diff.different, function (carry, path) {
            _.set(carry, path, _.get(updated, path));
            return carry;
        }, {});
    }

    platformIsSafari() {
        var vendor = _.get(this.window, 'navigator.vendor', '');
        var userAgent = _.get(this.window, 'navigator.userAgent', '');

        return ((/Apple/.test(vendor)) &&
            (!/CriOS/.test(userAgent)));
    }

    platformIsIOS() {
        var userAgent = _.get(this.window, 'navigator.userAgent', '');

        return ((/iPad|iPhone|iPod/.test(userAgent)) &&
            (!_.get(this.window, 'MSStream', false)));
    }

    platformIsiPhone() {
        var userAgent = _.get(this.window, 'navigator.userAgent', '');

        return ((/iPhone|iPod/.test(userAgent)) &&
            (!_.get(this.window, 'MSStream', false)));
    }

    getObjectDiff(a, b) {
        var result = {
            different: [],
            missing_from_first: [],
            missing_from_second: []
        };

        var self = this;
    
        _.reduce(a, function (result, value, key) {
            if (_.has(b, key)) {
                if (_.isEqual(value, b[key])) {
                    return result;
                } else {
                    if (typeof (a[key]) != typeof ({}) || typeof (b[key]) != typeof ({})) {
                        //dead end.
                        result.different.push(key);
                        return result;
                    } else {
                        var deeper = self.getObjectDiff(a[key], b[key]);
                        result.different = result.different.concat(_.map(deeper.different, function (sub_path) {
                            return key + "." + sub_path;
                        }));
    
                        result.missing_from_second = result.missing_from_second.concat(_.map(deeper.missing_from_second, function (sub_path) {
                            return key + "." + sub_path;
                        }));
    
                        result.missing_from_first = result.missing_from_first.concat(_.map(deeper.missing_from_first, function (sub_path) {
                            return key + "." + sub_path;
                        }));
    
                        return result;
                    }
                }
            } else {
                result.missing_from_second.push(key);
                return result;
            }
        }, result);
    
        _.reduce(b, function (result, value, key) {
            if (_.has(a, key)) {
                return result;
            } else {
                result.missing_from_first.push(key);
                return result;
            }
        }, result);
    
        return result;
    }
    
    // TODO Get rid of this method and its calls
    compareObjects(obj1: any, obj2: any) {
        if (obj1 == null || obj2 == null) {
            if (!(obj1 == null && obj2 == null)) {
                return false;
            }
        }
    
        //Loop properties on the first object
        for (var p in obj1) {
            if (p.substring(0, 1) != '$') {
                if (obj1.hasOwnProperty(p) !== obj2.hasOwnProperty(p)) {
                    return false;
                }
    
                switch (typeof (obj1[p])) {
    
                    case 'object':
                        //Recursion for deep compare
                        if (!this.compareObjects(obj1[p], obj2[p])) {
                            return false;
                        }
                        break;
    
                    case 'function':
                        if (typeof (obj2[p]) == 'undefined' || (p != 'compare' && obj1[p].toString() != obj2[p].toString())) {
                            return false;
                        }
                        break;
    
                    default:
                        if (obj1[p] != obj2[p]) {
                            return false;
                        }
    
                }
            }
        }
    
        //Check for properties in object 2 not present in object 1
        for (var p in obj2) {
            if (typeof (obj1[p]) == 'undefined') {
                return false;
            }
        }
        return true;
    }
}
